import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import {
  CreateProductCatalogDto,
  ProductCatalogDto,
  ProductPackageUom,
  UpdateProductCatalogDto,
} from '@services/dtos'
import { productCatalogSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { Grid, Tab, Tabs } from '@mui/material'
import {
  createProductCatalog,
  updateProductCatalog,
} from '@state/reducers/productCatalogReducer'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { FormCrudHeader } from '../FormCrudHeader'
import ProductCatalogSectionInfo from './sections/ProductCatalogSectionInfo'
import ProductCatalogSectionOrder from './sections/ProductCatalogSectionOrder'
import ErrorIcon from '@mui/icons-material/Error'
import { trim } from 'lodash'
import moment from 'moment'

enum Section {
  Info,
  Order,
}

type Props = {
  duplicateCase?: boolean
  editingProductCatalog?: ProductCatalogDto | null
  onClose: () => void
  defaultValues?: Partial<CreateProductCatalogDto>
}

const ProductCatalogForm: React.FC<Props> = ({
  editingProductCatalog,
  onClose,
  defaultValues,
  duplicateCase
}) => {
  const useCreateForm = useForm<CreateProductCatalogDto>({
    defaultValues: editingProductCatalog
      ? {
          supplierId: editingProductCatalog.supplier.id,
          productCode: editingProductCatalog.productCode,
          name: editingProductCatalog.name,
          type: editingProductCatalog.type,
          compoundId: editingProductCatalog.compound?.id,
          packageUom: editingProductCatalog.packageUom,
          unitsPerPack: editingProductCatalog.unitsPerPack,
          gtinEan: editingProductCatalog.gtinEan,
          primaryFamilyId: editingProductCatalog.productFamily?.id,
          requiresColdChain: editingProductCatalog.requiresColdChain,
          inUseStabilityMinCold: editingProductCatalog.inUseStabilityMinCold,
          inUseStabilityMinRoom: editingProductCatalog.inUseStabilityMinRoom,
          excursionOutOfColdMaxMin: editingProductCatalog.excursionOutOfColdMaxMin,
          notes: editingProductCatalog.notes,
          maxDivision: editingProductCatalog.maxDivision,
          supplyLeadTimeDays: editingProductCatalog.supplyLeadTimeDays,
          prices: editingProductCatalog.prices.map(price => ({
            ...price,
            startAt: price.startAt ? moment(price.startAt).format('YYYY-MM-DD') : undefined,
            endAt: moment(price.endAt).format('YYYY-MM-DD')
          })),
          genericName: editingProductCatalog.genericName,
          examIds: editingProductCatalog.exams.map((exam) => exam.id),
          validation: editingProductCatalog.validation
            ? {
                groupOperator: editingProductCatalog.validation.groupOperator,
                rules: editingProductCatalog.validation.rules.map(rule => ({
                  role: rule.role,
                  operator: rule.operator,
                  memberIds: rule.members.map(m => m.id.toString())
                }))
              }
            : {
                groupOperator: undefined,
                rules: [],
              },
          orderableIfExamAssociated: editingProductCatalog.orderableIfExamAssociated,
          generatorParentIsotopeId: editingProductCatalog.generatorParentIsotope?.id,
          generatorDaughterIsotopeId: editingProductCatalog.generatorDaughterIsotope?.id
      }
      : { 
          requiresColdChain: true, 
          packageUom: ProductPackageUom.UNIT, 
          validation: {
            groupOperator: undefined,
            rules: [],
          },
          ...defaultValues 
        },
    resolver: yupResolver(productCatalogSchema),
  })

  const dispatch = useAppDispatch()

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [section, setSection] = useState<Section>(Section.Info)
  const handleChangeSection = (_: React.SyntheticEvent, newSection: Section) => {
    setSection(newSection)
  }

  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      useCreateForm.reset()
    }
  }, [dispatch])

  const onSubmitForm = async (data: UpdateProductCatalogDto) => {
    if (duplicateCase && (trim(editingProductCatalog?.productCode) === trim(useCreateForm.watch('productCode')) 
        || trim(editingProductCatalog?.name) === trim(useCreateForm.watch('name')))) {
      return
    }
    const displayError = () => cb('Une erreur est survenue, vérifiez que le combo fournisseur / code produit n\'existe pas')

    if (editingProductCatalog && !duplicateCase) {
      dispatch(updateProductCatalog(
        { 
          id: editingProductCatalog.id, 
          dto: {
            ...data,
            primaryFamilyId: data.primaryFamilyId ?? null,
            compoundId: data.compoundId ?? null,
          }
        }
      ))
        .unwrap()
        .then(() => {
          cb('Le catalogue de produits a été modifié avec succès')
          onClose()
        })
        .catch(displayError)
        .finally(() => setIsSubmitting(false))
    } else {
      dispatch(createProductCatalog(data as CreateProductCatalogDto))
        .unwrap()
        .then(() => {
          cb(`Le catalogue de produits a été ${duplicateCase ? 'dupliqué' : 'créé'} avec succès`)
          onClose()
        })
        .catch(displayError)
        .finally(() => setIsSubmitting(false))
    }
  }

  const errorSectionInfo = [
    'type', 'productCode', 'name', 'compoundId', 
    'primaryFamilyId', 'requiresColdChain', 'inUseStabilityMinCold', 
    'inUseStabilityMinRoom', 'excursionOutOfColdMaxMin', 'notes', 'maxDivision', 'genericName', 'generatorParentIsotopeId', 'generatorDaughterIsotopeId'
  ].some(field => !!useCreateForm.formState.errors[field as keyof typeof useCreateForm.formState.errors])
  const errorSectionOrder = [
    'supplyLeadTimeDays', 'gtinEan', 'prices', 'validation', 'examIds', 'supplierId', 'packageUom', 'unitsPerPack',
  ].some(field => !!useCreateForm.formState.errors[field as keyof typeof useCreateForm.formState.errors])

  return (
    <div className="flex w-full h-full p-4">
      <form 
        onSubmit={(e) => {
          e.stopPropagation()
          useCreateForm.handleSubmit(onSubmitForm)(e)
        }}
        className="w-full">
        <FormCrudHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          editing={!!editingProductCatalog}
          duplicateCase={duplicateCase}
        />

        <div className="max-w-screen-md mx-auto mt-4">
          <fieldset className="border border-gray-200 p-4 rounded-lg">
            <legend className="text-lg font-bold mb-4 border border-gray-200 p-2 rounded-lg">
              Catalogue produits
            </legend>

            <Tabs
              value={section}
              onChange={handleChangeSection}
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
              className="mb-4 [&_.MuiTabs-flexContainer]:justify-center"
            >
              <Tab label="Caractéristiques" icon={errorSectionInfo ? <ErrorIcon /> : undefined} iconPosition='start' />
              <Tab label="Informations commande" icon={errorSectionOrder ? <ErrorIcon /> : undefined} iconPosition='start' />
            </Tabs>

            <Grid container rowSpacing={2} columnSpacing={4} mt={1}>
              {section === Section.Info && <ProductCatalogSectionInfo
                useCreateForm={useCreateForm}
                editingProductCatalog={editingProductCatalog}
                duplicateCase={duplicateCase}
              />}

              {section === Section.Order && <ProductCatalogSectionOrder
                useCreateForm={useCreateForm}
                editingProductCatalog={editingProductCatalog}
              />}
            </Grid>
          </fieldset>
        </div>
      </form>
    </div>
  )
}

export default ProductCatalogForm
